import React from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui";
import { useShareFormLink } from "../context/ShareFormLinkContext";

// Modular Step Imports
import { StepChooseForm } from "./steps/StepChooseForm";
import { StepChooseFan } from "./steps/StepChooseFan";
import { StepAIPersonalization } from "./steps/StepAIPersonalization";

export function CustomizerWorkflowWizard() {
  const {
    allowsAi,
    isEditingRequest,
    wizardStep,
    setWizardStep,
    handleNextStepFromFanSelection,
    isGenerating,
    handleGenerateLink,
    handleUpdateLink,
  } = useShareFormLink();

  const steps = allowsAi ? [1, 2, 3] : [1, 2];
  const isLastStep = wizardStep === (allowsAi ? 3 : 2);

  // Navigate forward
  const handleNextStep = async () => {
    if (wizardStep === 1) {
      const success = await handleNextStepFromFanSelection();
      if (!success) return;

      setWizardStep(2);
    } else if (wizardStep === 2) {
      if (!allowsAi) {
        if (isEditingRequest) {
          await handleUpdateLink();
        } else {
          await handleGenerateLink();
        }
      } else {
        setWizardStep(3);
      }
    } else if (wizardStep === 3) {
      if (isEditingRequest) {
        await handleUpdateLink();
      } else {
        await handleGenerateLink();
      }
    }
  };

  // Navigate backward
  const handlePrevStep = () => {
    if (wizardStep > 1) {
      setWizardStep(prev => prev - 1);
    }
  };

  return (
    <div className="flex flex-col gap-4 w-full text-left select-none animate-in slide-in-from-right-2 duration-300 font-sans">
      {/* Wizard Step Indicator Headers */}
      <div className="flex items-center gap-1.5 mb-1">
        {steps.map(s => (
          <div
            key={s}
            className={`h-2 flex-1 rounded-full transition-all duration-300 ${
              wizardStep === s ? "bg-primary shadow-xs" : wizardStep > s ? "bg-emerald-400" : "bg-slate-200"
            }`}
          />
        ))}
      </div>

      {/* Render Active Step Component */}
      <div className="min-h-[280px] flex flex-col justify-between">
        <div className="flex-1 pb-4">
          {wizardStep === 1 && <StepChooseFan />}
          {wizardStep === 2 && <StepChooseForm />}
          {wizardStep === 3 && allowsAi && <StepAIPersonalization />}
        </div>

        {/* Navigation Buttons Row */}
        <div className="flex justify-between items-center border-t border-slate-100 pt-4 shrink-0">
          {wizardStep > 1 ? (
            <Button
              type="button"
              variant="ghost"
              onClick={handlePrevStep}
              disabled={isGenerating}
              className="text-sm font-bold text-slate-500 hover:bg-slate-100 flex items-center gap-1.5 h-10 px-4 rounded-lg"
            >
              <ArrowLeft className="size-4" />
              Back
            </Button>
          ) : (
            <div />
          )}

          <Button
            type="button"
            onClick={handleNextStep}
            disabled={isGenerating}
            className="h-10 font-bold text-sm flex items-center gap-1.5 bg-primary text-white border-none hover:bg-primary/95 px-5 rounded-lg transition-colors shadow-sm"
          >
            {isLastStep ? (
              isEditingRequest ? (
                "Save Changes"
              ) : (
                "Create Customized Link"
              )
            ) : (
              <>
                Next
                <ArrowRight className="size-4" />
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
